import {
  Button,
  H4,
  H6,
  InputEmail,
  InputField,
  InputImage,
  InputLabel,
  InputOption,
  InputPhoneNumber,
  InputSelect,
  InputSwitch,
  InputText,
  InputTextArea,
} from "@sellar-io/components";
import { BusinessEntityType } from "@sellar-io/api-v2/types";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";
import { Icon } from "../../components";
import { Chip } from "~/components/chip/Chip";
import { type DeepKeys, useStore } from "@tanstack/react-form";
import { BUSINESS_ENTITY_TYPES } from "../../constants/sellarConstants.shared";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { generateFormPath } from "~/utils/formHelpers";
import { BuyerVenueTypesDialog } from "../buyer/Dialogs/BuyerVenueTypesDialog";
import { OwnershipType } from "../buyer/BuyerBusinessOwnership";
import { BuyerBusinessOwnershipGrouped } from "../buyer/BuyerBusinessOwnershipGrouped";
import { VenueType } from "../buyer/BuyerVenueSelection.shared";
import { BusinessFormCreateProps } from "./BusinessFormCreate";
import { useSellarUser } from "~/hooks";
import { isValidEmail } from "@sellar-io/common";
import {
  type BusinessFormApi,
  type BusinessFormFieldValues,
  type BusinessFormType,
} from "./BusinessFormValues";
import { BusinessAddressFormFragment } from "./BusinessAddressFormFragment";
import { BuyerFormFragment } from "../buyer/BuyerFormFragment";
import {
  fieldNote,
  getFieldError,
  invalidFieldClassName,
  requiredField,
} from "./formValidation";
import { useFirebaseImageUpload } from "~/hooks/useFirebaseImageUpload";

type BusinessFieldName = DeepKeys<BusinessFormFieldValues>;

const fieldName = (name: string) => name as BusinessFieldName;

export const BusinessFormFragment = ({
  form,
  path,
  type,
  lockEmail = false,
  requireEntityType = false,
  metadata,
}: {
  form: BusinessFormApi;
  path?: string;
  type: BusinessFormType;
  lockEmail?: boolean;
  requireEntityType?: boolean;
  metadata?: BusinessFormCreateProps["metadata"];
}): ReactElement => {
  const { business: userBusiness } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const uploadImage = useFirebaseImageUpload();
  const isSupplierCreatingBuyer = !!userBusiness?.Supplier && type === "buyer";
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [buyerVenueTypesDialogActive, setBuyerVenueTypesDialogActive] =
    useState(false);

  const isSupplierCreatingCustomer =
    isSupplierCreatingBuyer && metadata?.createdFrom === "Add customer";

  const hasLogoUpload = !isSupplierCreatingBuyer;
  const hasHeroImageUpload = type === "supplier";
  const hasMultipleImageUploads = hasLogoUpload && hasHeroImageUpload;

  const canDisplayInvoiceEmailsTextAreaField =
    metadata?.createdFrom !== "Business switcher" &&
    metadata?.createdFrom !== "Add customer";

  const canDisplaySingleInvoiceEmailInputField =
    metadata?.createdFrom === "Add customer" ||
    metadata?.createdFrom === "Business switcher";

  const businessEmail = useStore(form.store, (state) => state.values.email);
  const isInvoiceEmailAddressSame = useStore(
    form.store,
    (state) => state.values.isInvoiceEmailAddressSame,
  );
  const hasNoVatNumber = useStore(
    form.store,
    (state) => state.values.hasNoVatNumber,
  );
  const hasNoAwrsNumber = useStore(
    form.store,
    (state) => state.values.hasNoAwrsNumber,
  );
  const selectedEntityType = useStore(
    form.store,
    (state) => state.values.entityType,
  );
  const buyerOwnership = useStore(
    form.store,
    (state) => state.values.Retailer?.ownershipType,
  );

  useEffect(() => {
    if (user?.email && !businessEmail && lockEmail) {
      form.setFieldValue("email", user.email);
      form.setFieldValue("invoiceEmails", user.email);
    }
  }, [businessEmail, form, lockEmail, user]);

  return (
    <>
      <FormSection title="Business details">
        <div
          className={`grid gap-4${hasMultipleImageUploads ? " @lg:grid-cols-2" : ""}`}
        >
          {hasLogoUpload && (
            <InputField label={"Upload a logo"}>
              <form.Field name={fieldName(generateFormPath("logoUrl", path))}>
                {({ state, handleChange }) => (
                  <InputImage
                    value={String(state.value ?? "")}
                    onChange={handleChange}
                    uploadFile={uploadImage}
                  />
                )}
              </form.Field>
            </InputField>
          )}
          {hasHeroImageUpload && (
            <InputField label="Hero image">
              <form.Field name={fieldName("Supplier.imageUrl")}>
                {({ state, handleChange }) => (
                  <InputImage
                    value={String(state.value ?? "")}
                    onChange={handleChange}
                    uploadFile={uploadImage}
                  />
                )}
              </form.Field>
            </InputField>
          )}
        </div>

        {type === "buyer" && !userBusiness?.Supplier && (
          <>
            <InputLabel>Venue type</InputLabel>
            <div className="flex flex-wrap items-center gap-2">
              {userBusiness?.Retailer?.venueTypes.map((type) => (
                <Chip
                  key={type}
                  title={
                    VenueType.find((venueType) => venueType.value === type)
                      ?.title ?? type
                  }
                  value={type}
                  selected={false}
                  selectable={false}
                />
              ))}
              <Icon
                icon={"PENCIL"}
                className={"cursor-pointer"}
                onClick={() => setBuyerVenueTypesDialogActive(true)}
              />
            </div>
            <InputLabel>Select ownership</InputLabel>
            <BuyerBusinessOwnershipGrouped
              value={buyerOwnership}
              onChange={(event) =>
                form.setFieldValue(
                  "Retailer.ownershipType",
                  event.currentTarget.value as OwnershipType,
                )
              }
            />
          </>
        )}

        <div className="grid gap-4 @md:grid-cols-2">
          <TextField
            form={form}
            name={generateFormPath("name", path)}
            label={
              isSupplierCreatingCustomer
                ? "Customer's trading name"
                : "Trading name"
            }
            maxLength={140}
            required
          />
          <TextField
            form={form}
            name={generateFormPath("registeredCompanyName", path)}
            label={
              isSupplierCreatingCustomer
                ? "Customer's limited company name"
                : "Company name"
            }
            maxLength={140}
          />
        </div>

        {lockEmail ? (
          <InputField label={"Business email"}>
            <InputEmail disabled={true} placeholder={user?.email} />
          </InputField>
        ) : (
          <form.Field
            name={fieldName(generateFormPath("email", path))}
            validators={requiredField("Business email")}
          >
            {({ state, handleChange, handleBlur }) => {
              const errorMessage = getFieldError(state.meta.errors);

              return (
                <InputField
                  label={"Business email"}
                  className={errorMessage ? invalidFieldClassName : undefined}
                  note={fieldNote(undefined, errorMessage)}
                  containerClassName={
                    errorMessage
                      ? "border-red-300 focus-within:border-red-500"
                      : undefined
                  }
                >
                  <InputEmail
                    name={generateFormPath("email", path)}
                    value={String(state.value ?? "")}
                    onBlur={handleBlur}
                    onChange={(event) => {
                      const newEmail = event.target.value;
                      handleChange(newEmail);
                      if (canDisplaySingleInvoiceEmailInputField) {
                        form.setFieldValue("invoiceEmails", newEmail);
                      }
                    }}
                    placeholder={"example@email.com"}
                    required
                  />
                </InputField>
              );
            }}
          </form.Field>
        )}

        {canDisplayInvoiceEmailsTextAreaField && (
          <form.Field
            name="invoiceEmails"
            validators={{
              onSubmit: ({ value }) =>
                value.trim()
                  ? validateInvoiceEmails(value)
                  : "Invoice emails is required",
            }}
          >
            {({ state, handleChange, handleBlur }) => {
              const errorMessage = getFieldError(state.meta.errors);

              return (
                <InputField
                  label="Invoice emails"
                  className={errorMessage ? invalidFieldClassName : undefined}
                  note={fieldNote(
                    "Set this field to indicate to suppliers where you'd like invoices sent. Use a comma-separated list of email addresses.",
                    errorMessage,
                  )}
                  containerClassName={
                    errorMessage
                      ? "border-red-300 focus-within:border-red-500"
                      : undefined
                  }
                >
                  <InputTextArea
                    className="h-24 resize-none"
                    name="invoiceEmails"
                    value={state.value}
                    onBlur={handleBlur}
                    onChange={(event) => handleChange(event.target.value)}
                    placeholder="accounts@example.com,sales@example.com,invoices@example.com"
                    required
                  />
                </InputField>
              );
            }}
          </form.Field>
        )}
      </FormSection>

      {canDisplaySingleInvoiceEmailInputField && (
        <>
          <div>
            <div className="flex gap-2">
              <BooleanSwitch
                form={form}
                name="isInvoiceEmailAddressSame"
                onChange={(checked) => {
                  if (businessEmail && checked) {
                    form.setFieldValue("invoiceEmails", businessEmail);
                  }
                }}
              />
              <div>
                {isSupplierCreatingCustomer
                  ? "Use business email as invoice email"
                  : "My invoice email is the same as my business email"}
              </div>
            </div>
          </div>
          {isInvoiceEmailAddressSame && isSupplierCreatingCustomer && (
            <H6>
              Order updates and invoices will be sent to this customer's
              Business email. If you want to send their invoices to a different
              email address, disable the switch to input an Invoice email.
            </H6>
          )}
          {!isInvoiceEmailAddressSame && (
            <TextField
              form={form}
              type="email"
              name="invoiceEmails"
              label="Invoice email"
              note={
                isSupplierCreatingCustomer
                  ? "This is the email address you would like this customer's invoices to be sent to."
                  : "Set this field to indicate to the suppliers you order from where you'd like invoices sending to."
              }
            />
          )}
        </>
      )}

      <section className="border-t border-solid border-zinc-200 pt-5">
        <BusinessAddressFormFragment
          type={type}
          form={form}
          path={generateFormPath("addresses", path)}
          metadata={metadata}
        />
      </section>

      {type === "buyer" && (
        <>
          <InputField label="Delivery instructions">
            <form.Field
              name={fieldName(generateFormPath("deliveryInstructions", path))}
            >
              {({ state, handleChange, handleBlur }) => (
                <InputText
                  name={generateFormPath("deliveryInstructions", path)}
                  value={String(state.value ?? "")}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                  placeholder={
                    "e.g. Gate code 1234. Leave round the back if no one is in"
                  }
                />
              )}
            </form.Field>
          </InputField>
          <div className="text-zinc-500">
            {isSupplierCreatingCustomer
              ? "These instructions will be noted against this customer for your own reference"
              : "This will be displayed to suppliers on all of your new orders"}
          </div>
        </>
      )}

      <FormSection title="Contact and compliance">
        <TextField
          form={form}
          name={generateFormPath("phoneNumber", path)}
          label="Business phone number"
          input="phoneNumber"
        />

        {(!isSupplierCreatingBuyer || showAdditionalDetails) && (
          <>
            <div className="grid gap-4 @md:grid-cols-2">
              <TextField
                form={form}
                name={generateFormPath("vatNumber", path)}
                label="VAT number"
                disabled={hasNoVatNumber}
                required={!isSupplierCreatingBuyer && !hasNoVatNumber}
              />
              <TextField
                form={form}
                name={generateFormPath("awrsNumber", path)}
                label="AWRS number"
                disabled={hasNoAwrsNumber}
                required={!isSupplierCreatingBuyer && !hasNoAwrsNumber}
              />
            </div>

            <div className="grid gap-3 @md:grid-cols-2">
              <SwitchRow>
                <BooleanSwitch
                  form={form}
                  name={generateFormPath("hasNoVatNumber", path)}
                  onChange={(hasNoVatNumber) => {
                    if (hasNoVatNumber) {
                      form.setFieldValue(
                        fieldName(generateFormPath("vatNumber", path)),
                        "",
                      );
                    }
                  }}
                />
                <div>
                  {isSupplierCreatingCustomer
                    ? "Customer doesn't have a VAT number"
                    : "I don't have a VAT number"}
                </div>
              </SwitchRow>

              <SwitchRow>
                <BooleanSwitch
                  form={form}
                  name={generateFormPath("hasNoAwrsNumber", path)}
                  onChange={(hasNoAwrsNumber) => {
                    if (hasNoAwrsNumber) {
                      form.setFieldValue(
                        fieldName(generateFormPath("awrsNumber", path)),
                        "",
                      );
                    }
                  }}
                />
                <div>
                  {isSupplierCreatingCustomer
                    ? "Customer doesn't have an AWRS number"
                    : "I don't have an AWRS number"}
                </div>
              </SwitchRow>
            </div>

            <div className="grid gap-4 @md:grid-cols-2">
              <form.Field
                name={fieldName(generateFormPath("entityType", path))}
                validators={
                  requireEntityType || !userBusiness?.Supplier
                    ? requiredField("Business entity type")
                    : undefined
                }
              >
                {({ state, handleChange, handleBlur }) => {
                  const errorMessage = getFieldError(state.meta.errors);

                  return (
                    <InputField
                      label={"Business entity type"}
                      className={
                        errorMessage ? invalidFieldClassName : undefined
                      }
                      note={fieldNote(undefined, errorMessage)}
                      containerClassName={
                        errorMessage
                          ? "border-red-300 focus-within:border-red-500"
                          : undefined
                      }
                    >
                      <InputSelect
                        name={generateFormPath("entityType", path)}
                        value={String(state.value ?? "")}
                        onBlur={handleBlur}
                        onChange={(event) =>
                          handleChange(
                            event.target.value as BusinessEntityType | "",
                          )
                        }
                        required={requireEntityType || !userBusiness?.Supplier}
                      >
                        <InputOption
                          value={""}
                          disabled={
                            requireEntityType || !userBusiness?.Supplier
                          }
                          hidden={requireEntityType || !userBusiness?.Supplier}
                        >
                          Choose...
                        </InputOption>
                        {Object.keys(BUSINESS_ENTITY_TYPES).map((key) => (
                          <InputOption
                            key={
                              BUSINESS_ENTITY_TYPES[
                                key as keyof typeof BUSINESS_ENTITY_TYPES
                              ].value
                            }
                            value={
                              BUSINESS_ENTITY_TYPES[
                                key as keyof typeof BUSINESS_ENTITY_TYPES
                              ].value
                            }
                          >
                            {
                              BUSINESS_ENTITY_TYPES[
                                key as keyof typeof BUSINESS_ENTITY_TYPES
                              ].name
                            }
                          </InputOption>
                        ))}
                      </InputSelect>
                    </InputField>
                  );
                }}
              </form.Field>

              {["llp", "limited"].includes(selectedEntityType) && (
                <TextField
                  form={form}
                  name={generateFormPath("companyNumber")}
                  label="Company number"
                  required={!isSupplierCreatingBuyer}
                />
              )}
            </div>

            {isSupplierCreatingBuyer && (
              <BuyerFormFragment form={form} path={"Retailer"} />
            )}
          </>
        )}
      </FormSection>

      {(!isSupplierCreatingBuyer || showAdditionalDetails) && (
        <FormSection title="Online presence">
          <div className="grid gap-4 @md:grid-cols-3">
            <TextField
              form={form}
              name={generateFormPath("websiteUrl", path)}
              label="Website"
              placeholder="https://sellar.io"
            />
            <TextField
              form={form}
              name={generateFormPath("instagram", path)}
              label="Instagram handle"
              placeholder="sellar.io"
            />
            <TextField
              form={form}
              name={generateFormPath("twitter", path)}
              label="Twitter handle"
              placeholder="sellarapp"
            />
          </div>
        </FormSection>
      )}

      {isSupplierCreatingBuyer && (
        <>
          {showAdditionalDetails && (
            <InputField label={"Upload a logo"}>
              <form.Field name={fieldName(generateFormPath("logoUrl", path))}>
                {({ state, handleChange }) => (
                  <InputImage
                    value={String(state.value ?? "")}
                    onChange={handleChange}
                    uploadFile={uploadImage}
                  />
                )}
              </form.Field>
            </InputField>
          )}
          {!showAdditionalDetails && (
            <Button
              className={"text-blue-800"}
              color={"tertiary"}
              onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
            >
              <Icon icon={showAdditionalDetails ? "MINUS" : "PLUS"} />
              Additional details
            </Button>
          )}
        </>
      )}
      <BuyerVenueTypesDialog
        active={buyerVenueTypesDialogActive}
        onDismiss={() => setBuyerVenueTypesDialogActive(false)}
      />
    </>
  );
};

const TextField = ({
  form,
  name,
  label,
  note,
  placeholder,
  maxLength,
  required = false,
  disabled = false,
  type = "text",
  input = "text",
}: {
  form: BusinessFormApi;
  name: string;
  label: string;
  note?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  type?: "text" | "email";
  input?: "text" | "phoneNumber";
}) => (
  <form.Field
    name={fieldName(name)}
    validators={required && !disabled ? requiredField(label) : undefined}
  >
    {({ state, handleChange, handleBlur }) => {
      const errorMessage = getFieldError(state.meta.errors);
      const containerClassName =
        [
          input === "phoneNumber" ? "overflow-visible py-3" : undefined,
          errorMessage
            ? "border-red-300 focus-within:border-red-500"
            : undefined,
        ]
          .filter(Boolean)
          .join(" ") || undefined;

      return (
        <InputField
          label={label}
          className={errorMessage ? invalidFieldClassName : undefined}
          note={fieldNote(note, errorMessage)}
          containerClassName={containerClassName}
        >
          {(() => {
            const inputProps = {
              name,
              value: String(state.value ?? ""),
              onBlur: handleBlur,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                handleChange(event.target.value),
              placeholder,
              maxLength,
              required,
              disabled,
            };

            if (input === "phoneNumber") {
              return (
                <InputPhoneNumber
                  {...inputProps}
                  onChange={(phone) => handleChange(phone)}
                />
              );
            }

            if (type === "email") {
              return <InputEmail {...inputProps} />;
            }

            return <InputText {...inputProps} />;
          })()}
        </InputField>
      );
    }}
  </form.Field>
);

export const BooleanSwitch = ({
  form,
  name,
  disabled,
  onChange,
}: {
  form: BusinessFormApi;
  name: string;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}) => (
  <form.Field name={fieldName(name)}>
    {({ state, handleChange }) => (
      <InputSwitch
        checked={Boolean(state.value)}
        disabled={disabled}
        onChange={(value) => {
          handleChange(value);
          onChange?.(value);
        }}
      />
    )}
  </form.Field>
);

const validateInvoiceEmails = (value: string) => {
  const emails = value.split(",").map((email) => email.trim());

  for (const email of emails) {
    if (email.length === 0) {
      continue;
    }
    if (!isValidEmail(email)) {
      return `Invalid email: ${email}`;
    }
  }
  return undefined;
};

const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="@container flex flex-col gap-4 border-t border-solid border-zinc-200 pt-5 first:border-t-0 first:pt-0">
    <H4 className="text-zinc-900 dark:text-zinc-100">{title}</H4>
    {children}
  </section>
);

const SwitchRow = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-10 items-center gap-2 text-sm text-zinc-800">
    {children}
  </div>
);
